// Simple Instructor Matching (lightweight) DTOs matching backend SimpleInstructorSlotMetrics contract
// Backend source: MyLanguageBird.DTO.SimpleInstructorSlotMetrics / SimpleInstructorMatchResult
// NOTE: Backend uses PascalCase, Angular's HttpClient automatically converts to camelCase

import { IUserTag } from './iuser-tag.interface';

export interface IFeasibilityDiagnostics {
	matchedPatternElements?: number;
	totalPatternElements?: number;
	matchedUniqueDayCount?: number;
	isPatternFeasible?: boolean;
	requiredFrequency?: number;
	meetsFrequency?: boolean;
	frequencyCoverageRatio?: number;
	patternCoverageJson?: string;
	missingPatternElementsJson?: string;
	conflictsEvaluated?: boolean;
	conflictSlotCount?: number;
	matchedConflictSlotCount?: number;
	conflictLessonCount?: number;
	conflictRate?: number;
	hasConflicts?: boolean;
	hasMatchedConflicts?: boolean;
	conflictSamplesJson?: string;
	totalPatternSlotsEvaluated?: number;
	conflictSlotsZero?: number;
	conflictSlotsOne?: number;
	conflictSlotsTwo?: number;
	conflictSlotsThreeOrMore?: number;
	gatedOutSlotsOverThreshold?: number;
	realizableOccurrenceCount?: number;
	driftLostOccurrenceCount?: number;
	driftLostRatio?: number;
	maxPositiveDriftMinutes?: number;
	maxNegativeDriftMinutes?: number;
	realizableOccurrenceCountDynamic?: number;
	driftLostOccurrenceCountDynamic?: number;
	driftLostRatioDynamic?: number;
	maxPositiveDriftMinutesDynamic?: number;
	maxNegativeDriftMinutesDynamic?: number;
}

/**
 * Slot-level aggregated metrics for a single perspective (availability or committed).
 * Backend: SimpleSlotMetric
 */
export interface ISimpleSlotMetric {
	/** Number of matched slots in the pattern */
	matchedSlots: number;
	/** Number of slots with conflicts */
	conflictSlots: number;
	/** Number of distinct days covered */
	distinctDays: number;
	/** Percentage of matched slots over total evaluated slots (0-100) */
	coverageRatio: number;
	/** Percentage of slots with conflicts (0-100), lower is better */
	conflictRate: number;
}

/**
 * Aggregated instructor metrics separated by Availability vs Committed perspectives.
 * Backend: SimpleInstructorSlotMetrics (auto-converted from PascalCase to camelCase by HttpClient)
 */
/**
 * Language and level qualification for an instructor.
 * Backend: InstructorLanguageLevel
 */
export interface IInstructorLanguageLevel {
	languageID: number;
	languageName: string;
	languageLevelID: number;
	levelName: string;
}

export interface ISimpleInstructorSlotMetrics {
	// Core identity
	instructorUserID: number;
	instructorName: string;
	firstName?: string;
	lastName?: string;
	fullName?: string;
	email?: string;
	preferredName?: string;
	username?: string;

	// Instructor details
	instructorIndex?: number;
	timezoneID?: number;
	timeZoneName?: string;
	timezoneDisplayName?: string;
	timezoneAbbreviation?: string;
	timezoneHasDST?: boolean;
	utcOffset?: number;
	activeEnrollmentCount?: number;
	languagesAndLevels?: IInstructorLanguageLevel[];

	// Qualification flags
	supportsLanguageLevel: boolean;
	hasBasicAvailability: boolean;

	// Metrics by perspective
	availability: ISimpleSlotMetric;
	committed: ISimpleSlotMetric;
	requiredFrequency: number;

	// Drift / realization metrics
	driftAffectedOccurrences: number;
	maxPositiveDriftMinutes: number;
	maxNegativeDriftMinutes: number;

	// Composite Scoring & Sustainability
	compositeScore: number;
	minWeeksFullyCoverable: number;
	weekSustainabilityRatio: number;
	fitLevel: 'high' | 'medium' | 'low'; // Calculated fit level based on matched slots vs required frequency

	// Optional diagnostics
	feasibility?: IFeasibilityDiagnostics;

	// Auto-escalation flag
	autoEscalatedToFullDuration: boolean;
}

/**
 * Result wrapper for simplified matching endpoint.
 * Backend: SimpleInstructorMatchResult (auto-converted from PascalCase to camelCase by HttpClient)
 */
export interface ISimpleInstructorMatchResult {
	enrollmentID: number;
	windowStartUtc: string;
	windowEndUtc: string;
	anchorTimezoneID?: number;
	anchorTimezoneName?: string;
	anchorTimezoneUtcOffsetHours?: number;
	requiredFrequency: number;
	analysisPhase: string;
	fullEnrollmentDurationDays?: number;
	instructors: ISimpleInstructorSlotMetrics[];

	// Optional debug fields
	anchorPatternDebugJson?: string;
	instructorSlotStatusDebugJson?: string;
	feasibilityGapInstructorIDsJson?: string;
}
